import "@app/App.css";
import { Button } from "@comps/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { Separator } from "@comps/ui/separator";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import Field, { AggregationType, FieldType, hasFormatting, validAggregationByType } from "@dto/fields";
import { View } from "@dto/views";
import { BASE_URL } from "@utils/constants";
import { clearData } from "@utils/data";
import { fieldToPath, getPathKeys, getShownPathKeys, pathKey } from "@utils/model";
import { toastError } from "@utils/toasts";
import { TW_AppDialogButton, TW_ButtonList, TW_FieldMenuPopoverContent, TW_FieldSection, TW_FieldViewMenuButtonNotDisabled, getWsButtonStyle, useHover } from "@utils/twClasses";
import { put } from "@utils/utils";
import { EllipsisVertical } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useDataStore, useModelStore } from "src/store";
import AggregationMenuItem from "./AggregationMenuItem";
import { FieldFormatSection } from "./FieldFormatSection";

interface ViewFieldMenuButtonProps {
  field: Field;
  index: number;
  visible: boolean;
  positionStyle?: React.CSSProperties;
  stickable?: boolean;
  hiddable?: boolean;
}

function ViewFieldMenuButton({ field, index, visible, positionStyle = undefined, stickable = true, hiddable = true }: ViewFieldMenuButtonProps) {

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
  const sharedObjectInfos = useModelStore((state) => state.sharedObjectInfos);
  const relationByKeys = useModelStore((state) => state.relationByKeys);
  const fieldByKeys = useModelStore((state) => state.fieldByKeys);
  const selectedView = useModelStore((state) => state.selectedView);
  const updateView = useModelStore((state) => state.updateSelectedView);
  const workspace = useModelStore((state) => state.selectedWorkspace);
  const rowActionsEnabled = useDataStore(state => state.rowActionsEnabled);
  const { t } = useTranslation();
  const pathKeys = useMemo(() => getPathKeys(selectedObjectInfos, selectedObjectInfos?.fields || [], relationByKeys, fieldByKeys, true), [selectedObjectInfos, relationByKeys, fieldByKeys]);
  
  const pKey = useMemo(() => {

    if (!selectedObjectInfos?.object._key || !selectedView) {
      return null;
    }

    const path = fieldToPath(selectedObjectInfos?.object._key, relationByKeys, fieldByKeys, field);
    if (path) {
      return pathKey(path);
    }
    return null;
  }, [selectedObjectInfos, relationByKeys, fieldByKeys, field]);


  if (!selectedObjectInfos?.object._key || !selectedView || !workspace) {
    return null;
  }

  var disabled = !rowActionsEnabled;
  var currentAggregation: AggregationType | undefined;
  if (!pKey) {
    disabled = true;
  } else {
    currentAggregation = selectedView?.aggregations?.get(pKey) ?? field.aggregation;
  }

  const closeMenu = () => {
    setIsPopoverOpen(false);
  }

  const isDisabled = !rowActionsEnabled;
  const onAggregationChange = (aggregationType: AggregationType | null) => {
    if (aggregationType === currentAggregation) {
      return;
    }

    const newViewAggregations = (selectedView?.aggregations ? new Map(selectedView?.aggregations) : new Map<string, AggregationType>());
    if (!pKey) {
      return;
    }
    if (!aggregationType) {
      newViewAggregations.delete(pKey);
    } else {
      newViewAggregations.set(pKey, aggregationType);
    }
    const newView: View = { ...selectedView, aggregations: newViewAggregations };

    put<View>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${selectedObjectInfos?.object?._key}/views`, newView).then(response => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            toastError(translate(t, cError.i18n, cError.params));
          } else {
            toastError(cError.message);
          }
        }
      } else {
        updateView(selectedObjectInfos, newView, null, sharedObjectInfos);
        closeMenu();
        clearData();
      }
    })

  }

  const isSticked = stickable && selectedView?.stickyPathKey === pKey;

  const onStickChange = () => {

    let newView: View;
    if (isSticked) {
      newView = { ...selectedView, stickyPathKey: null };
    } else {
      newView = { ...selectedView, stickyPathKey: pKey };
    }

    put<View>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${selectedObjectInfos?.object?._key}/views`, newView).then(response => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            toastError(translate(t, cError.i18n, cError.params));
          } else {
            toastError(cError.message);
          }
        }
      } else {
        updateView(selectedObjectInfos, newView, null, sharedObjectInfos);
        closeMenu();
      }
    })
  }

  const validateFormat = (format: Map<string, any> | undefined) => {

    if (!pKey) {
      return;
    }

    const newFormats = new Map<string, Map<string, any>>(selectedView?.formats ?? new Map<string, Map<string, any>>());
    if (!format) {
      newFormats.delete(pKey);
    } else {
      newFormats.set(pKey, format);
    }

    const newView: View = { ...selectedView, formats: newFormats };

    put<View>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${selectedObjectInfos?.object?._key}/views`, newView).then(response => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            toastError(translate(t, cError.i18n, cError.params));
          } else {
            toastError(cError.message);
          }
        }
      } else {
        updateView(selectedObjectInfos, newView, null, sharedObjectInfos);
        closeMenu();
      }
    })
  }

  const onHideClick = () => {

      if (!workspace?._key) {
        console.error("Workspace key is not set");
        return;
      }

      if (!pKey) {
        console.error("Path key is not set");
        return;
      }

      const shownPathKeys = getShownPathKeys(selectedView, pathKeys.filter(pathKey => pathKey !== null) as string[]);
      const newShownPaths = shownPathKeys.filter(pathKey => pathKey !== pKey);
      const newSelectedView = { ...selectedView, shownPaths: newShownPaths, hiddenPaths: null };
  
      put<View>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${selectedObjectInfos?.object?._key}/views`, newSelectedView).then(response => {
        if (response) {
          if (isCompozzError(response)) {
            const cError = response as CompozzError;
            if (cError.i18n) {
              toastError(translate(t, cError.i18n, cError.params));
            } else {
              toastError(cError.message);
            }
          }
        } else {
          updateView(selectedObjectInfos, newSelectedView, null, sharedObjectInfos);
          closeMenu();
        }
      })
  
  }

  const setPopoverOpen = (open: boolean) => {
    setIsPopoverOpen(open);
  }


  const positionStyleToUse = positionStyle || {
    position: "absolute",
    right: "0.5rem",
  } as React.CSSProperties;
  return (
    <Popover open={isPopoverOpen} onOpenChange={setPopoverOpen} >
      <PopoverTrigger disabled={disabled} className={TW_FieldViewMenuButtonNotDisabled + ((visible || isPopoverOpen) && !isDisabled ? " opacity-100" : " opacity-0")} style={positionStyleToUse}>
        <EllipsisVertical className={"bg-gray-100"} />
      </PopoverTrigger>
      <ViewFieldPopoverContent pathKey={pKey} field={field} index={index} onAggregationChange={onAggregationChange} currentAggregation={currentAggregation} onStickChange={onStickChange} isSticked={isSticked} validateFormat={validateFormat} stickable={stickable} hiddable={hiddable} onHideClick={onHideClick} />
    </Popover>
  )
}

type ViewFieldPopoverContentProps = {
  pathKey: string | null;
  field: Field;
  index: number;
  onAggregationChange: (aggregationType: AggregationType | null) => void;
  currentAggregation: AggregationType | undefined;
  onStickChange: () => void;
  isSticked: boolean;
  validateFormat: (format: Map<string, any> | undefined) => void;
  stickable?: boolean;
  onHideClick: () => void;
  hiddable?: boolean;
}

function ViewFieldPopoverContent({ pathKey, field, index, onAggregationChange, currentAggregation, onStickChange, isSticked, validateFormat, stickable = true, hiddable = true, onHideClick }: ViewFieldPopoverContentProps) {
  const validAggregationTypes = useMemo(() => validAggregationByType(field.type, field.typeItems), [field.type, field.typeItems]);
  const isLinkColumn = field.type === FieldType.Link;
  const { t } = useTranslation();
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const selectedView = useModelStore(state => state.selectedView);
  const [newFormat, setNewFormat] = useState(selectedView?.formats?.get(pathKey ?? "") ?? field.format);

  const cancel = () => {
    setIsDialogOpen(false);
    setNewFormat(selectedView?.formats?.get(pathKey ?? "") ?? field.format);
  }

  const onSubmit = () => {
    validateFormat(newFormat);
  }

  const onFormatChange = (key: string, isCheckbox: boolean) => {
    return (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      let newValue: any = event.target.value;
      if (isCheckbox && event.target instanceof HTMLInputElement) {
        newValue = event.target.checked;
      }
      const updatedFormat = new Map<string, any>(newFormat);
      updatedFormat.set(key, newValue);
      setNewFormat(updatedFormat);
    }
  }

  return (
    <div>
      <PopoverContent className={TW_FieldMenuPopoverContent} >
        {stickable === true && <p className="text-sm font-medium text-black cursor-pointer hover:bg-gray-100 p-1 rounded-md" onClick={onStickChange}>{t("field.view.menu." + (isSticked ? "unstick" : "stick"))}</p>}
        {hasFormatting(field) && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <p className="text-sm font-medium text-black cursor-pointer hover:bg-gray-100 p-1 rounded-md">
                {t("field.view.menu.formatting")}
              </p>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("field.view.menu.formatting")}</DialogTitle>
              </DialogHeader>
              <div className={TW_FieldSection}>
                <FieldFormatSection fieldType={field.type} fieldTypeItems={field.typeItems} format={newFormat} aggregation={currentAggregation ?? field.aggregation} onFormatChange={onFormatChange} />
              </div>
              <div className={TW_ButtonList}>
                <Button className={TW_AppDialogButton} onClick={cancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
                <Button className={TW_AppDialogButton} onClick={onSubmit} {...okHover}>{t("general.button.ok.text")}</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
        {hiddable === true && <p className="text-sm font-medium text-black cursor-pointer hover:bg-gray-100 p-1 rounded-md" onClick={onHideClick}>{t("field.view.menu.hide")}</p>}
        {(stickable === true || hasFormatting(field) || hiddable === true) && <Separator className="my-2" />}
        <p className="text-sm font-medium text-black">{t("field.view.menu.aggregation")}</p>
        <ul>
          {validAggregationTypes.map((aggregationType) => (
            <AggregationMenuItem key={aggregationType} aggregationType={aggregationType} onAggregationChange={onAggregationChange} currentAggregation={currentAggregation ?? null} isLinkColumn={isLinkColumn} />
          ))}
          <AggregationMenuItem key={null} aggregationType={null} onAggregationChange={onAggregationChange} currentAggregation={currentAggregation ?? null} isLinkColumn={isLinkColumn} />
        </ul>
      </PopoverContent>
    </div>
  )
}

export default ViewFieldMenuButton;
